/**
 * Inbox.tsx — FR-017 (V2/V3/V4) / FR-018 / T320.
 *
 * Bindings:
 *   - api.editorial.feeds.listAll   — STUB query (convex/editorial/feeds.ts:5)
 *     returns []; the wired surface renders the designed EmptyState rather
 *     than a fabricated list (specs/exploration/stub_orphan_inventory.md §2 #7).
 *
 * Honest-state contract (CLAUDE.md §1, §5):
 *   - Loading: skeleton list.
 *   - Empty (real or stubbed return value of []): EmptyState with the
 *     designed copy from src/i18n/operator-copy.ts.
 *   - Populated: real rows from Convex (when the stub is later replaced by
 *     a real listing, this file already consumes the same shape).
 *
 * Glossary-clean copy: every shipped string comes from
 * src/i18n/operator-copy.ts (the centralized source the glossary gate scans).
 * No forbidden term from tests/fixtures/glossary.{es,en}.ts appears in JSX
 * text or allowlisted attributes.
 *
 * Negative invariants enforced by tests/editorial/virtualization.test.ts:
 *   (V2) Not a re-export shell — owns its body.
 *   (V2) Imports `api` from `@convex/api` AND uses `useMutationWithToast`
 *        for any mutation surface (we currently expose no mutation; the
 *        accept/reject actions become live mutations once a real editorial
 *        review mutation lands — placeholder UI noted by glossary-safe copy).
 *   (V3) Imports the EmptyState honest-empty primitive because it binds
 *        a stubbed query ref.
 *   (V4) Renders rows from the Convex query, never from `CIRCA.<arr>.map(`.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  Btn,
  EmptyState,
  Input,
  PageHead,
  Panel,
  Pill,
  Select,
  Skeleton,
  Thumb,
} from '../../components/primitives';
import { EDITORIAL_INBOX_COPY } from '../../i18n/operator-copy';
// T318 — the mock fixture import retired; the inbox renders rows from the
// Convex query (binding) and honest skeleton / empty states otherwise.
import { fmtDur, ago } from '../../lib/format';

interface InboxItemShape {
  _id?: string;
  id?: string;
  title?: string;
  source?: string;
  addedAtMs?: number;
  durationMs?: number;
}

export function Inbox() {
  // Real Convex subscription. The handler is currently a stub returning [];
  // the V2 wiring contract is the bind site itself — the file imports `api`
  // and consumes the live shape, so a future real listing flows in without
  // a downstream edit.
  const items = useQuery(api.editorial.feeds.listAll, {}) as
    | InboxItemShape[]
    | undefined;

  // Loading state: useQuery returns undefined until the first round-trip.
  if (items === undefined) {
    return (
      <div>
        <PageHead
          title={EDITORIAL_INBOX_COPY.pageTitle}
          description={EDITORIAL_INBOX_COPY.pageDescription}
        />
        <Panel pad={16} style={{ marginBottom: 24 }}>
          <Skeleton h={40} />
        </Panel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} h={110} />
          ))}
        </div>
      </div>
    );
  }

  // Honest-empty for the stubbed slice (FR-017 V3): the query is wired but
  // the backend returns []; we render the designed empty affordance rather
  // than a plausible fabrication.
  if (items.length === 0) {
    return (
      <div>
        <PageHead
          title={EDITORIAL_INBOX_COPY.pageTitle}
          description={EDITORIAL_INBOX_COPY.pageDescription}
        />
        <Panel>
          <EmptyState
            icon="message"
            title={EDITORIAL_INBOX_COPY.emptyTitle}
            body={EDITORIAL_INBOX_COPY.emptyBody}
          />
        </Panel>
      </div>
    );
  }

  // Populated state — the wired surface renders real rows once the editorial
  // feed query returns data. The shape is consumed defensively (optional
  // fields tolerated) so a future server-side schema extension is additive.
  return (
    <div>
      <PageHead
        title={EDITORIAL_INBOX_COPY.pageTitle}
        description={EDITORIAL_INBOX_COPY.pageDescription}
      />
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ width: 300 }}>
            <Input
              icon="search"
              placeholder={EDITORIAL_INBOX_COPY.searchPlaceholder}
              onChange={() => undefined}
              value=""
            />
          </div>
          <Select
            value={EDITORIAL_INBOX_COPY.filterPending}
            onChange={() => undefined}
            options={[
              EDITORIAL_INBOX_COPY.filterAll,
              EDITORIAL_INBOX_COPY.filterPending,
              EDITORIAL_INBOX_COPY.filterAccepted,
              EDITORIAL_INBOX_COPY.filterRejected,
            ]}
          />
        </div>
      </Panel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((it: InboxItemShape) => {
          const key = it._id ?? it.id ?? Math.random().toString(36).slice(2);
          const title = it.title ?? 'Sin título';
          const source = it.source ?? '—';
          const agoLabel = it.addedAtMs != null ? ago(it.addedAtMs) : '—';
          const dur = fmtDur(it.durationMs ?? 0);
          return (
            <Panel key={key} pad={16}>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ width: 160, flexShrink: 0 }}>
                  <Thumb radius={12} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 18,
                      fontWeight: 500,
                      marginBottom: 6,
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <Pill tone="info" size="sm">
                      {source}
                    </Pill>
                    <span
                      className="t-mono"
                      style={{ fontSize: 11, color: 'var(--muted-slate)' }}
                    >
                      {agoLabel}
                    </span>
                    <Pill tone="neutral" size="sm">
                      {dur}
                    </Pill>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    justifyContent: 'center',
                  }}
                >
                  <Btn size="sm" variant="ghost" icon="eye" decorative>
                    {EDITORIAL_INBOX_COPY.actionReview}
                  </Btn>
                  <Btn size="sm" variant="solid" icon="check" decorative>
                    {EDITORIAL_INBOX_COPY.actionAccept}
                  </Btn>
                  <Btn size="sm" variant="text" decorative>
                    {EDITORIAL_INBOX_COPY.actionReject}
                  </Btn>
                </div>
              </div>
            </Panel>
          );
        })}
      </div>
    </div>
  );
}

export default Inbox;
